/**
 * Notification Modal Manager
 *
 * Provides a branded modal notification system to replace browser alerts.
 * Supports success, error, warning, and info notification types.
 *
 * @example
 * import { NotificationModal } from '@theme/notification-modal';
 *
 * NotificationModal.success('Registration successful!');
 * NotificationModal.error('Please select a ticket first');
 * NotificationModal.warning('You can only select 3 classes');
 * NotificationModal.info('Please check your selections');
 */

/**
 * Notification type definitions
 * @typedef {'success' | 'error' | 'warning' | 'info'} NotificationType
 */

class NotificationModalManager {
  constructor() {
    this.modalElement = null;
    this.dialogComponent = null;
    this.messageElement = null;
    this.iconElement = null;
    this.buttonElement = null;
    this.initialized = false;
  }

  /**
   * Initialize the notification modal
   * @private
   */
  #initialize() {
    if (this.initialized) return;

    this.modalElement = document.querySelector('#notification-modal');
    if (!this.modalElement) {
      console.error('[NotificationModal] Modal element not found. Make sure to include the notification-modal snippet.');
      return;
    }

    this.dialogComponent = this.modalElement;
    this.messageElement = this.modalElement.querySelector('[data-notification-message]');
    this.iconElement = this.modalElement.querySelector('[data-notification-icon]');
    this.buttonElement = this.modalElement.querySelector('[data-notification-button]');

    this.initialized = true;
  }

  /**
   * Get the icon SVG for a notification type
   * @param {NotificationType} type - The notification type
   * @returns {string} SVG markup for the icon
   * @private
   */
  #getIcon(type) {
    const icons = {
      success: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>`,
      error: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>`,
      warning: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>`,
      info: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>`
    };

    return icons[type] || icons.info;
  }

  /**
   * Show a notification
   * @param {string} message - The message to display
   * @param {NotificationType} type - The notification type
   * @returns {Promise<void>}
   */
  async show(message, type = 'info') {
    this.#initialize();

    if (!this.initialized) {
      // Fallback to alert if modal not available
      console.warn('[NotificationModal] Falling back to alert');
      alert(message);
      return;
    }

    // Update message
    this.messageElement.textContent = message;

    // Update icon
    this.iconElement.innerHTML = this.#getIcon(type);

    // Remove all type classes
    const dialog = this.modalElement.querySelector('dialog');
    dialog.classList.remove(
      'notification-modal--success',
      'notification-modal--error',
      'notification-modal--warning',
      'notification-modal--info'
    );

    // Add the appropriate type class
    dialog.classList.add(`notification-modal--${type}`);

    // Ensure single button mode (reset from confirm mode if needed)
    const singleButton = this.modalElement.querySelector('[data-notification-button]');
    const confirmButtonsContainer = this.modalElement.querySelector('[data-confirm-buttons]');
    if (singleButton) singleButton.hidden = false;
    if (confirmButtonsContainer) confirmButtonsContainer.hidden = true;

    // Show the dialog
    if (this.dialogComponent && typeof this.dialogComponent.showDialog === 'function') {
      this.dialogComponent.showDialog();
    } else {
      console.error('[NotificationModal] Dialog component not properly initialized');
      alert(message); // Fallback
    }

    // Return a promise that resolves when the dialog is closed
    return new Promise((resolve) => {
      const handleClose = () => {
        this.modalElement.removeEventListener('dialog:close', handleClose);
        resolve();
      };
      this.modalElement.addEventListener('dialog:close', handleClose);
    });
  }

  /**
   * Show a success notification
   * @param {string} message - The success message
   * @returns {Promise<void>}
   */
  async success(message) {
    return this.show(message, 'success');
  }

  /**
   * Show an error notification
   * @param {string} message - The error message
   * @returns {Promise<void>}
   */
  async error(message) {
    return this.show(message, 'error');
  }

  /**
   * Show a warning notification
   * @param {string} message - The warning message
   * @returns {Promise<void>}
   */
  async warning(message) {
    return this.show(message, 'warning');
  }

  /**
   * Show an info notification
   * @param {string} message - The info message
   * @returns {Promise<void>}
   */
  async info(message) {
    return this.show(message, 'info');
  }

  /**
   * Show a confirmation dialog with confirm/cancel buttons
   * Returns an object with confirmed status and a done() function to close the modal
   * @param {string} message - The confirmation message
   * @param {Object} [options] - Configuration options
   * @param {string} [options.confirmText='Confirm'] - Text for confirm button
   * @param {string} [options.cancelText='Cancel'] - Text for cancel button
   * @param {string} [options.loadingText='Processing...'] - Text shown while loading
   * @param {boolean} [options.showLoading=true] - Whether to show loading state on confirm
   * @param {NotificationType} [options.type='warning'] - The notification type
   * @returns {Promise<{confirmed: boolean, done: Function}>} Object with confirmed status and done() to close modal
   */
  async confirm(message, options = {}) {
    const {
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      loadingText = 'Processing...',
      showLoading = true,
      type = 'warning'
    } = options;

    this.#initialize();

    if (!this.initialized) {
      // Fallback to native confirm if modal not available
      console.warn('[NotificationModal] Falling back to native confirm');
      const confirmed = window.confirm(message);
      return { confirmed, done: () => {} };
    }

    // Update message
    this.messageElement.textContent = message;

    // Update icon
    this.iconElement.innerHTML = this.#getIcon(type);

    // Remove all type classes
    const dialog = this.modalElement.querySelector('dialog');
    dialog.classList.remove(
      'notification-modal--success',
      'notification-modal--error',
      'notification-modal--warning',
      'notification-modal--info'
    );
    dialog.classList.add(`notification-modal--${type}`);

    // Switch to confirm mode: hide single button, show confirm/cancel buttons
    const singleButton = this.modalElement.querySelector('[data-notification-button]');
    let confirmButtonsContainer = this.modalElement.querySelector('[data-confirm-buttons]');

    if (singleButton) {
      singleButton.hidden = true;
    }

    // Create confirm buttons if they don't exist
    if (!confirmButtonsContainer) {
      confirmButtonsContainer = document.createElement('div');
      confirmButtonsContainer.className = 'notification-modal__confirm-buttons';
      confirmButtonsContainer.dataset.confirmButtons = '';
      confirmButtonsContainer.innerHTML = `
        <button type="button" class="notification-modal__button notification-modal__button--cancel" data-confirm-cancel>
          ${this.#escapeHTML(cancelText)}
        </button>
        <button type="button" class="notification-modal__button notification-modal__button--confirm" data-confirm-confirm>
          ${this.#escapeHTML(confirmText)}
        </button>
      `;

      const footer = this.modalElement.querySelector('.notification-modal__footer');
      if (footer) {
        footer.appendChild(confirmButtonsContainer);
      }
    } else {
      // Update button text
      const cancelBtn = confirmButtonsContainer.querySelector('[data-confirm-cancel]');
      const confirmBtn = confirmButtonsContainer.querySelector('[data-confirm-confirm]');
      if (cancelBtn) cancelBtn.textContent = cancelText;
      if (confirmBtn) confirmBtn.textContent = confirmText;
      confirmButtonsContainer.hidden = false;
    }

    // Show the dialog
    if (this.dialogComponent && typeof this.dialogComponent.showDialog === 'function') {
      this.dialogComponent.showDialog();
    } else {
      console.error('[NotificationModal] Dialog component not properly initialized');
      const confirmed = window.confirm(message);
      return { confirmed, done: () => {} };
    }

    // Return a promise that resolves based on which button was clicked
    return new Promise((resolve) => {
      const cancelBtn = confirmButtonsContainer.querySelector('[data-confirm-cancel]');
      const confirmBtn = confirmButtonsContainer.querySelector('[data-confirm-confirm]');

      const cleanup = () => {
        cancelBtn?.removeEventListener('click', handleCancel);
        confirmBtn?.removeEventListener('click', handleConfirm);
        this.modalElement.removeEventListener('dialog:close', handleClose);
      };

      const resetAndClose = () => {
        cleanup();
        // Reset to single button mode
        if (singleButton) singleButton.hidden = false;
        if (confirmButtonsContainer) confirmButtonsContainer.hidden = true;
        // Close the dialog using the correct method name
        if (this.dialogComponent && typeof this.dialogComponent.closeDialog === 'function') {
          this.dialogComponent.closeDialog();
        }
      };

      const showLoadingState = () => {
        // Update message to loading state
        this.messageElement.textContent = loadingText;
        // Update icon to spinner
        this.iconElement.innerHTML = this.#getLoadingIcon();
        // Hide buttons during loading
        if (confirmButtonsContainer) confirmButtonsContainer.hidden = true;
      };

      const handleCancel = () => {
        resetAndClose();
        resolve({ confirmed: false, done: () => {} });
      };

      const handleConfirm = () => {
        cleanup();
        if (showLoading) {
          // Show loading state for async operations
          showLoadingState();
          // Return done function to close modal after async operation
          resolve({
            confirmed: true,
            done: resetAndClose
          });
        } else {
          // Close immediately for synchronous operations
          resetAndClose();
          resolve({
            confirmed: true,
            done: () => {}
          });
        }
      };

      const handleClose = () => {
        // Dialog closed via ESC/backdrop - treat as cancel
        cleanup();
        if (singleButton) singleButton.hidden = false;
        if (confirmButtonsContainer) confirmButtonsContainer.hidden = true;
        resolve({ confirmed: false, done: () => {} });
      };

      cancelBtn?.addEventListener('click', handleCancel);
      confirmBtn?.addEventListener('click', handleConfirm);
      this.modalElement.addEventListener('dialog:close', handleClose);
    });
  }

  /**
   * Get loading spinner icon SVG
   * @returns {string} SVG markup for loading spinner
   * @private
   */
  #getLoadingIcon() {
    return `<svg class="notification-modal__spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 2V6M12 18V22M6 12H2M22 12H18M19.07 4.93L16.24 7.76M7.76 16.24L4.93 19.07M19.07 19.07L16.24 16.24M7.76 7.76L4.93 4.93" />
    </svg>`;
  }

  /**
   * HTML escape helper to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   * @private
   */
  #escapeHTML(str) {
    if (str == null) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
  }
}

// Create and export singleton instance
export const NotificationModal = new NotificationModalManager();

// Also make it available globally for inline scripts
if (typeof window !== 'undefined') {
  window.NotificationModal = NotificationModal;
}
